/* ============================================================
   MOSAIK — ZOOM / PAN on the plan

   Two independent layers, one per input device, sharing the same CSS
   transform on the <canvas> (they can never run at once — each is gated
   by the mobile media query):

     - TOUCH  (≤768px)   pinch / drag / finger-paint          — see below
     - MOUSE  (≥769px)   wheel zoom, hand-tool drag, toolbar  — see the
                         "DESKTOP" section at the bottom of this file

   ------------------------------------------------------------
   TOUCH ZOOM / PAN / FINGER-PAINT  (mobile only)

   Lets the visitor zoom into the tile grid with the fingers to draw
   precisely, WITHOUT the browser zooming the whole page.

   Gestures on the canvas:
     - 1 doigt          PEINT toujours — tape comme glissé, à n'importe quel
                        zoom. Il n'y a plus d'outil « main » au doigt : le doigt
                        posé sur le plan ne fait qu'une chose, et le visiteur n'a
                        aucun mode à armer pour dessiner.
     - 2 doigts         DÉPLACENT le plan si on les fait glisser du même côté,
                        ZOOMENT si on les écarte ou les resserre — jamais les
                        deux à la fois (voir « verrou » ci-dessous).
     - mode « Sélectionner une zone » armé : 1 doigt TRACE LE CADRE au lieu de
       peindre (mosaik-select.js) — c'est le seul moment où le plan ne se peint
       pas au doigt, et le bouton du mode le dit.

   LE VERROU DU GESTE À DEUX DOIGTS. Les deux doigts posés, on attend de savoir
   ce qu'ils veulent : leur ÉCARTEMENT change-t-il (pincement) ou leur MILIEU
   se déplace-t-il (glissement) ? Le premier des deux à franchir son seuil prend
   le geste jusqu'au doigt levé. Sans ce verrou, un déplacement emporte toujours
   un peu de zoom et un pincement un peu de déplacement : le plan glissait sous
   les doigts pendant qu'on cherchait juste à zoomer.

   Two-finger drag pans at every zoom level — including 1:1 — and the pan is
   allowed to over-shoot the canvas edges by OVERPAN of its width on each side.
   That lets the visitor slide a side border away from the phone's edge toward
   the centre of the screen, where it is far more comfortable to colour.

   The zoom is a CSS transform on the <canvas>; painting maps the touch
   point back to canvas-internal pixels via getBoundingClientRect(), so it
   stays accurate at any zoom level. Desktop is untouched (guarded by the
   mobile media query — handlers no-op above 768px).

   ------------------------------------------------------------
   SHARPNESS. A CSS transform only magnifies the bitmap the plan was drawn
   into, so both layers call reRender() once the gesture settles: the renderer
   then redraws the SAME layout into a bitmap sized for the new zoom (see
   renderScale in mosaik-render.js). The transform itself stays untouched, so
   the zoom is smooth while it happens and sharpens when it stops.
   ============================================================ */
window.MosaikZoom = {

  MAX: 4,
  OVERPAN: 0.5,                      // extra pan allowed past each side, as a fraction of canvas width
                                     // (0.5 = a side column can be dragged all the way to screen centre)
  m: null, cv: null, frame: null,
  scale: 1, tx: 0, ty: 0,
  _pinch: null,
  _mode: null,                       // 'paint' | 'select'
  _startX: 0, _startY: 0,
  // Seuils du verrou à deux doigts (voir l'en-tête). L'écartement est le plus
  // exigeant des deux : deux doigts qui glissent ne restent jamais parfaitement
  // parallèles, et sans cette marge le moindre gauchissement passerait pour un
  // pincement.
  PINCH_LOCK: 16,                    // px de variation d'écartement avant de dire « zoom »
  PAN_LOCK: 10,                      // px de déplacement du milieu avant de dire « déplacement »

  isMobile:function(){ return window.matchMedia('(max-width:768px)').matches; },

  /* Une transformation est-elle en place, sur la couche active ? Sert à
     mosaik-ui.js : à la souris, le zoom fait grandir le cadre du plan
     (_dFrameHeight), donc la scène — un observateur de taille ne doit pas
     prendre ce redimensionnement-là pour un changement de fenêtre. */
  zoomed:function(){
    const s  = this.isMobile() ? this.scale : this.dScale,
          tx = this.isMobile() ? this.tx    : this.dTx,
          ty = this.isMobile() ? this.ty    : this.dTy;
    return Math.abs(s - 1) > 1e-4 || Math.abs(tx) > .5 || Math.abs(ty) > .5;
  },

  setup:function(mosaik){
    this.m = mosaik;
    const cv = mosaik.grid;
    if(!cv) return;
    this.cv = cv;
    this.frame = cv.parentElement;     // #mosaik-canvas — grown in height (only) while zoomed
    const opt = { passive:false };
    cv.addEventListener('touchstart',  e => this.onStart(e), opt);
    cv.addEventListener('touchmove',   e => this.onMove(e),  opt);
    cv.addEventListener('touchend',    e => this.onEnd(e),   opt);
    cv.addEventListener('touchcancel', e => this.onEnd(e),   opt);
    // grid geometry changes -> zoom no longer valid, go back to 1:1
    ['mosaik-collection','mosaik-area-width','mosaik-area-height'].forEach(id => {
      const el = document.getElementById(id);
      if(el) el.addEventListener('change', () => this.reset());
    });
    window.addEventListener('resize', () => this.reset());
    this.setupDesktop();
  },

  /* ---- transform helpers ---- */
  apply:function(){
    this.cv.style.transform = 'translate(' + this.tx + 'px,' + this.ty + 'px) scale(' + this.scale + ')';
    // la barre de zoom est montrée au doigt aussi : chaque geste (pincement,
    // déplacement, bouton) peut rendre un de ses boutons inutile, et c'est dSync
    // qui le dit. Au bureau c'est dApplyClamped qui l'appelle.
    this.dSync();
    this.reRender();
  },

  /* The zoom factor currently applied to the canvas, whichever layer holds it.
     mosaik-render.js sizes the bitmap from this: the transform is a pure upscale,
     so the plan is only ever as sharp as the bitmap it was drawn into. */
  currentScale:function(){
    return this.isMobile() ? (this.scale || 1) : (this.dScale || 1);
  },

  /* La transformation EN PLACE, sur la couche active : échelle et déplacement.
     Les règles graduées en vivent — fixées au panneau, elles doivent savoir où
     le plan est passé pour poser leurs crans en face des bonnes dalles (voir
     syncRulers dans mosaik-render.js). */
  viewport:function(){
    const mob = this.isMobile();
    return { s:  (mob ? this.scale : this.dScale) || 1,
             tx: (mob ? this.tx    : this.dTx)    || 0,
             ty: (mob ? this.ty    : this.dTy)    || 0 };
  },

  /* Ask the renderer for a bitmap that matches the zoom we just reached — but
     only once the gesture settles: a full redraw per wheel tick / pinch frame
     would stutter on a big floor. Until then the CSS transform keeps showing the
     previous bitmap upscaled, so the zoom itself stays perfectly smooth.
     RE_DELAY is short enough to read as "it sharpens as I stop". */
  RE_DELAY: 140,
  reRender:function(){
    const m = this.m;
    if(!m || !m.draw || !m.renderScale) return;
    clearTimeout(this._reTimer);
    this._reTimer = setTimeout(() => {
      const L = m.layout;
      if(!L) return;
      const want = m.renderScale(L.cW, L.cH), have = m.rscale || 1;
      // redraw only when it buys something: rounding noise must not trigger one
      if(Math.abs(want - have) / Math.max(want, have) > 0.02) m.draw();
    }, this.RE_DELAY);
  },
  /* LE PANNEAU NE GRANDIT PLUS AVEC LE ZOOM (au doigt).
     Il le faisait — _growHeight l'allongeait jusqu'à +50 % — du temps où le
     mobile était une page qui défilait d'un bloc : le plan zoomé s'y étalait et
     on descendait dans la page pour en voir le bas. L'écran est maintenant
     partagé (le plan, la fenêtre des réglages, la barre d'action — §4 du CSS) :
     le panneau a une hauteur à lui, et l'allonger déborderait sur les réglages.
     C'est le plan qui se déplace DANS le panneau, ce que _clamp sait déjà faire
     — la même chose que sur l'axe horizontal, où le panneau n'a jamais grandi. */

  /* Le panneau est en border-box et réserve en padding la bande de la règle du
     haut : une hauteur mesurée doit la déduire, sinon la fenêtre du plan est
     comptée plus haute qu'elle n'est et le plan se range sous la règle. */
  _padY:function(){
    const inner = (this.m && this.m.frameInner && this.frame)
      ? this.m.frameInner(this.frame) : null;
    return inner ? inner.padY : 0;
  },
  /* Back to 1:1 for BOTH layers — they share the one CSS transform, so a reset
     has to clear whichever of them is currently holding it. */
  reset:function(){
    this.scale = 1; this.tx = 0; this.ty = 0;
    this.dScale = 1; this.dTx = 0; this.dTy = 0;
    // retour à l'état de départ : la main de la souris n'est plus armée (au doigt
    // elle ne l'est jamais — voir dSync)
    this.panTool = false;
    if(this.cv) this.cv.style.transform = '';
    if(this.frame) this.frame.style.height = '';
    this.dSync();
  },
  _dist:function(a, b){ const dx = a.clientX - b.clientX, dy = a.clientY - b.clientY; return Math.hypot(dx, dy); },
  _clamp:function(tx, ty){
    const bw = this.cv.clientWidth, bh = this.cv.clientHeight, s = this.scale;
    // la fenêtre visible, bandes des règles déduites (voir _viewBox)
    const b = this.frame ? this._viewBox() : null;
    const fw = (b && b.w) ? b.w : bw;
    const fh = (b && b.h) ? b.h : bh;
    const over = fw * this.OVERPAN;                     // horizontal breathing room on each side
    // Horizontally we allow the canvas to slide `over` px past each edge, so a
    // side column can be pulled toward the screen centre. Vertically the scaled
    // canvas must still cover the (taller) frame — no over-shoot there.
    //
    // `fw - bw*s` et non `bw*(1-s)` : les deux ne coïncident que tant que le plan
    // fait la largeur de son panneau. Il peut désormais être DESSINÉ plus large
    // (dalles trop petites autrement — voir mobileLayout dans mosaik-render.js),
    // et ce débordement-là est précisément ce qu'il faut pouvoir ramener à
    // l'écran : mesuré sur le panneau, il l'est.
    const minTx = Math.min(0, fw - bw * s) - over, maxTx = over;
    const minTy = fh - bh * s;
    return [Math.min(maxTx, Math.max(minTx, tx)), Math.min(0, Math.max(minTy, ty))];
  },
  /* touch point -> canvas-internal CSS pixels (accounts for the transform).
     clientWidth/Height, not width/height: the bitmap is now drawn at a multiple
     of the layout size (see renderScale), and paintAt() speaks CSS pixels. */
  _toInternal:function(clientX, clientY){
    const r = this.cv.getBoundingClientRect();
    return { x: (clientX - r.left) / r.width * this.cv.clientWidth,
             y: (clientY - r.top)  / r.height * this.cv.clientHeight };
  },

  paintClient:function(clientX, clientY){
    const p = this._toInternal(clientX, clientY);
    if(this.m.paintAt(p.x, p.y)) this.m.draw();
    this._lastPaint = p;                              // anchor for the next drag segment
  },

  /* sampling step (internal px) along a drag — about half a tile, so no tile in
     the swept segment is ever skipped. Falls back to a small constant pre-layout. */
  _paintStep:function(){
    const L = this.m.layout;
    if(L && L.tileW && L.tileH) return Math.max(4, Math.min(L.tileW, L.tileH) * 0.5);
    return 6;
  },

  /* Paint every tile along the segment from the last painted point to the current
     one. touchmove only fires a handful of times per second, so a finger moving at
     a normal pace lands far-apart samples; without interpolation the tiles between
     them are skipped (why a slow stroke was needed). We walk the segment and redraw
     once at the end. */
  paintStroke:function(clientX, clientY){
    const p = this._toInternal(clientX, clientY);
    const last = this._lastPaint;
    if(!last){ if(this.m.paintAt(p.x, p.y)) this.m.draw(); this._lastPaint = p; return; }
    const dx = p.x - last.x, dy = p.y - last.y;
    const n = Math.min(120, Math.max(1, Math.ceil(Math.hypot(dx, dy) / this._paintStep())));
    let changed = false;
    for(let i = 1; i <= n; i++){
      if(this.m.paintAt(last.x + dx * (i / n), last.y + dy * (i / n))) changed = true;
    }
    if(changed) this.m.draw();
    this._lastPaint = p;
  },

  /* Le mode « Sélectionner une zone » est-il armé ? Le doigt y trace un cadre
     au lieu de peindre — c'est mosaik-select.js qui tient le geste, ce module
     ne fait que le lui passer. */
  selecting:function(){ return !!(window.MosaikSelect && MosaikSelect.armed); },

  /* ---- gestures ---- */
  onStart:function(e){
    if(!this.isMobile()) return;
    if(e.touches.length === 1){
      e.preventDefault();
      this._pinch = null;
      const t = e.touches[0];
      this._startX = t.clientX; this._startY = t.clientY;
      // zone à délimiter : le doigt ancre un coin, il ne pose pas de dalle.
      if(this.selecting()){
        this._mode = 'select';
        MosaikSelect.touchBegin(t.clientX, t.clientY);
        return;
      }
      /* Un objet posé sur le plan sous le doigt (ou un article armé qui attend
         d'être posé) : le geste lui revient — mosaik-objects.js répond `true`
         quand il le prend, et le doigt ne peint pas. Il rend `false` sur du sol
         nu, où l'on retombe sur le pinceau habituel. */
      if(window.MosaikObjects && MosaikObjects.enabled
         && MosaikObjects.touchBegin(t.clientX, t.clientY)){
        this._mode = 'object';
        return;
      }
      // nouveau geste : la gomme redécide de son sens sur la première dalle
      // touchée (mosaik-controller.js, `stroke`)
      if(this.m) this.m.stroke = null;
      /* UN DOIGT PEINT, TOUJOURS. Le zoom ne change plus rien à ce que fait le
         doigt : à quelque échelle que ce soit, il pose des dalles, tape comme
         glissé. Le plan se déplace à deux doigts (voir onMove) — c'est ce qui a
         permis de retirer l'outil « main » du mobile, avec le mode qu'il fallait
         armer puis désarmer pour dessiner à nouveau.

         Mais la dalle de départ n'est PAS posée ici. Deux doigts ne se posent
         jamais ensemble : le premier arrive quelques millisecondes avant l'autre,
         et peindre tout de suite salissait le plan d'une dalle à chaque
         déplacement. On attend donc de savoir : le premier mouvement d'un doigt
         seul la pose (onMove), le doigt levé sans mouvement aussi (onEnd), et un
         deuxième doigt annule tout — le geste était un déplacement. */
      this._mode = 'paint';
      this._painted = false;
      this._lastPaint = null;
    } else if(e.touches.length === 2){
      e.preventDefault();
      // un deuxième doigt : ce n'était pas un tracé de zone, c'est un geste à deux
      if(this._mode === 'select' && window.MosaikSelect) MosaikSelect.abortDraft();
      // ...ni un objet qu'on déplaçait : il retourne d'où il vient
      if(this._mode === 'object' && window.MosaikObjects) MosaikObjects.cancel();
      // ...ni un trait : ce qui vient d'être peint reste, mais le doigt ne peint
      // plus tant que les deux sont posés
      this._mode = null;
      this._lastPaint = null;
      const a = e.touches[0], b = e.touches[1], r = this.cv.getBoundingClientRect();
      this._pinch = {
        lock: null,                                                          // 'pan' | 'zoom', décidé au premier mouvement franc
        dist: this._dist(a, b), scale: this.scale, tx: this.tx, ty: this.ty,
        mx0: (a.clientX + b.clientX) / 2, my0: (a.clientY + b.clientY) / 2,  // milieu de départ : la référence des deux mesures
        layoutLeft: r.left - this.tx, layoutTop: r.top - this.ty   // untransformed origin, fixed during gesture
      };
    }
  },

  onMove:function(e){
    if(!this.isMobile()) return;
    if(e.touches.length === 2 && this._pinch){
      e.preventDefault();
      const a = e.touches[0], b = e.touches[1], p = this._pinch;
      const dist = this._dist(a, b);
      const mx = (a.clientX + b.clientX) / 2, my = (a.clientY + b.clientY) / 2;

      /* LE VERROU. Tant qu'aucune des deux mesures n'a franchi son seuil, le plan
         ne bouge pas d'un pixel — mieux vaut un instant d'immobilité qu'un zoom
         parasite au moment où l'on voulait glisser. Le seuil atteint, on REPART
         de l'état courant : le geste reprend là où il en est plutôt que de
         rattraper d'un bond les pixels consommés à décider. */
      if(!p.lock){
        const dd = Math.abs(dist - p.dist),
              dm = Math.hypot(mx - p.mx0, my - p.my0);
        if(dd < this.PINCH_LOCK && dm < this.PAN_LOCK) return;
        p.lock = (dd >= this.PINCH_LOCK && dd > dm) ? 'zoom' : 'pan';
        p.dist = dist; p.mx0 = mx; p.my0 = my;
        p.scale = this.scale; p.tx = this.tx; p.ty = this.ty;
      }

      // DÉPLACEMENT — les deux doigts sont partis du même côté : le plan les suit,
      // à l'échelle où il est. Rien d'autre ne change.
      if(p.lock === 'pan'){
        const c = this._clamp(p.tx + (mx - p.mx0), p.ty + (my - p.my0));
        this.tx = c[0]; this.ty = c[1];
        this.apply();
        return;
      }

      // ZOOM — seule l'échelle change. Le point du plan qui était sous le milieu
      // des doigts y reste (c'est ce qui donne un pincement « autour de ce que je
      // regarde »), mais ce milieu est celui du DÉBUT du geste : suivre le milieu
      // courant reviendrait à déplacer le plan, et le verrou dit non.
      let s = p.scale * (dist / p.dist);
      // plancher = mMin() et non 1 : le plan peut être dessiné plus large que son
      // panneau (mosaik-render.js), et on doit pouvoir le resserrer pour le voir
      // en entier. Il vaut 1 dès qu'il y tient déjà.
      s = Math.max(this.mMin(), Math.min(this.MAX, s));
      const k = s / p.scale;
      const tx = p.mx0 - p.layoutLeft - k * (p.mx0 - p.layoutLeft - p.tx);
      const ty = p.my0 - p.layoutTop  - k * (p.my0 - p.layoutTop - p.ty);
      this.scale = s;
      const c = this._clamp(tx, ty); this.tx = c[0]; this.ty = c[1];
      this.apply();
      return;
    }
    if(e.touches.length === 1){
      const t = e.touches[0];
      if(this._mode === 'select'){                     // le cadre suit le doigt
        e.preventDefault();
        MosaikSelect.touchMove(t.clientX, t.clientY);
      } else if(this._mode === 'object'){              // l'objet suit le doigt
        e.preventDefault();
        MosaikObjects.touchMove(t.clientX, t.clientY);
      } else if(this._mode === 'paint'){               // le trait suit le doigt, à tout zoom
        e.preventDefault();
        // le doigt bouge seul : c'était bien un tracé, la dalle de départ est
        // posée maintenant (voir onStart)
        if(!this._painted){ this._painted = true; this.paintClient(this._startX, this._startY); }
        this.paintStroke(t.clientX, t.clientY);        // interpolate so fast strokes skip no tile
      }
    }
  },

  onEnd:function(e){
    // doigt levé sur un cadre : la zone est prise, rien n'a été peint
    if(this._mode === 'select'){
      if(window.MosaikSelect) MosaikSelect.touchEnd();
      this._mode = null; this._pinch = null;
      return;
    }
    // doigt levé sur un objet : il est posé là (ou simplement sélectionné si le
    // doigt n'a pas bougé). Les coordonnées viennent de changedTouches — le
    // doigt n'est plus dans `touches`.
    if(this._mode === 'object'){
      const t = (e.changedTouches && e.changedTouches[0]) || null;
      if(window.MosaikObjects) MosaikObjects.touchEnd(t ? t.clientX : this._startX,
                                                      t ? t.clientY : this._startY);
      this._mode = null; this._pinch = null;
      return;
    }
    // une TAPE : le doigt est parti sans bouger, la dalle qu'il visait est posée
    // à ce moment-là (voir onStart)
    if(this._mode === 'paint' && !this._painted){
      this._painted = true;
      this.paintClient(this._startX, this._startY);
    }
    // Fin du geste : le doigt qui vient de peindre — une dalle tapée ou tout un
    // trait — fait UNE entrée d'historique (mosaik-controller.js). Un pincement
    // ou un déplacement n'a rien changé au plan : le commit reste sans effet.
    if(this.m && this.m.commitHistory) this.m.commitHistory();
    if(e.touches.length === 0){ this._mode = null; this._pinch = null; }
    else if(e.touches.length === 1){ this._pinch = null; this._mode = null; }
  },

  /* ============================================================
     LA BARRE DE ZOOM AU DOIGT  (≤768px)

     TROIS boutons ici (−, +, ajuster) : la main reste à la souris, où elle est le
     seul moyen de déplacer le plan. Au doigt il y en a un meilleur — deux doigts
     qui glissent — et un mode à armer puis désarmer pour redessiner ne valait pas
     le geste qu'il remplaçait. Le CSS mobile masque donc la pastille (voir
     #mosaik-zoom-tools dans le template), et mAction ignore son action.

     Le pincement reste le geste rapide, mais rien ne le dit à l'écran — et depuis
     que le plan peut être DESSINÉ plus large que son panneau (voir mobileLayout
     dans mosaik-render.js), il faut un moyen évident de le revoir en entier :
     c'est « ajuster », qui descend l'échelle sous 1 pour y arriver.

     Ces boutons pilotent la couche TACTILE (scale / tx / ty). La couche souris
     (dScale) ne tourne pas ici : dAction() aiguille sur isMobile(), et dSync()
     lit les valeurs de la couche active pour l'état des boutons.
     ============================================================ */

  /* Échelle à laquelle tout le plan tient dans le panneau : 1 quand il y tient
     déjà, en dessous quand il a été dessiné plus grand.
     LES DEUX AXES, désormais. La hauteur ne comptait pas tant que le panneau
     suivait celle du plan ; il a maintenant la sienne (l'écran du téléphone est
     partagé, §4 du CSS) et un sol tout en longueur passe donc aussi sous son
     bord bas — « ajuster » doit le ramener entier, sinon le bouton ment.
     La hauteur est mesurée BANDES DÉDUITES (frameInner) : la règle du haut est
     opaque, ce qui passe dessous n'est pas montré. */
  mMin:function(){
    const cv = this.cv, f = this.frame;
    if(!cv || !f || !cv.clientWidth || !cv.clientHeight) return 1;
    const inner = (this.m && this.m.frameInner) ? this.m.frameInner(f) : null;
    const fh = inner ? inner.h : f.clientHeight;
    const k = Math.min((f.clientWidth - 6) / cv.clientWidth,
                       (fh - 6) / cv.clientHeight);
    return Math.min(1, Math.max(0.15, k));
  },

  /* Reste-t-il du plan hors du panneau à ramener à l'écran ? */
  mPannable:function(){
    const cv = this.cv, f = this.frame, s = this.scale;
    if(!cv || !f) return false;
    const inner = (this.m && this.m.frameInner) ? this.m.frameInner(f) : null;
    return cv.clientWidth  * s > f.clientWidth + 1
        || cv.clientHeight * s > (inner ? inner.h : f.clientHeight) + 1;
  },

  /* Même maths que dZoomAt, sur la couche tactile : le point (clientX, clientY)
     reste sous lui-même pendant que l'échelle change. */
  mZoomAt:function(clientX, clientY, factor){
    const s0 = this.scale;
    const s = Math.max(this.mMin(), Math.min(this.MAX, s0 * factor));
    if(Math.abs(s - s0) < 1e-4) return;
    const r = this.cv.getBoundingClientRect();
    const layoutLeft = r.left - this.tx, layoutTop = r.top - this.ty;
    const k = s / s0;
    this.scale = s;
    const c = this._clamp(clientX - layoutLeft - k * (clientX - layoutLeft - this.tx),
                          clientY - layoutTop  - k * (clientY - layoutTop  - this.ty));
    this.tx = c[0]; this.ty = c[1];
    this.apply();
  },

  /* Le déplacement horizontal qui pose le plan — à l'échelle courante — au MILIEU
     du panneau. Le panneau du téléphone n'est pas une boîte flex (il ne peut pas
     l'être : le plan y est volontairement dessiné plus large que lui, et doit
     alors partir du bord gauche — voir mobileLayout dans mosaik-render.js), donc
     rien ne centre le <canvas> quand il est plus étroit. C'est fait ici.
     `- offsetLeft` : tx s'ajoute à la position de mise en page du canvas, ce
     n'est pas une position absolue. */
  mCenterX:function(){
    const cv = this.cv, f = this.frame;
    if(!cv || !f) return 0;
    return (f.clientWidth - cv.clientWidth * this.scale) / 2 - cv.offsetLeft;
  },

  mAction:function(kind){
    if(!this.cv || !this.frame) return;
    if(kind === 'pan') return;          // pas de main au doigt : deux doigts déplacent
    if(kind === 'fit'){
      const s = this.mMin();
      // le plan tient déjà : on repart de l'état intact, sans échelle résiduelle
      // qui décalerait de quelques pixels ce qui était en place
      if(s >= 1) this.reset();
      else this.scale = s;
      // Puis on recentre, DANS LES DEUX CAS. Un plan plus étroit que le panneau
      // (peu de dalles, ou plus haut que large — c'est alors la hauteur qui borne
      // l'ajustement) restait sinon collé à gauche avec toute la marge du même
      // côté : « ajuster à la fenêtre » doit le poser au milieu, pas seulement le
      // rentrer dans le cadre.
      const c = this._clamp(this.mCenterX(), 0);
      this.tx = c[0]; this.ty = c[1];
      this.apply();
      return;
    }
    // zoom depuis le centre du panneau, ce qu'on attend d'un bouton
    const r = this.frame.getBoundingClientRect();
    this.mZoomAt(r.left + r.width / 2, r.top + r.height / 2,
                 kind === 'in' ? this.D_STEP : 1 / this.D_STEP);
  },

  /* ============================================================
     DESKTOP  (≥769px) — wheel zoom, hand-tool pan, toolbar

     1:1 is the state the plan is DRAWN in, and the transform is cleared entirely
     there — so an untouched configurator behaves exactly as it did before this
     layer existed. From 1:1 the visitor can magnify up to D_MAX, and go DOWN to
     dMin(), which is below 1 only when the drawn plan is bigger than its panel
     (mosaik-geometry.js floors tiles at 50px so they stay clickable, which on a
     long floor overflows). That is what the "fit" button is for.

     #mosaik-canvas is the viewport (overflow:hidden, position:relative) and the
     <canvas> transform-origin is its top-left, so the canvas' offsetLeft /
     offsetTop inside the viewport ARE its untransformed position — offsets are
     not affected by transforms, which is what makes the clamp below stable
     while a drag is in flight.

     Painting is untouched: MouseEvent.offsetX/Y are reported in the element's
     own (untransformed) coordinate space, which is exactly the canvas-internal
     pixel space the controller paints in. The controller only has to skip the
     clicks that belong to a pan — hence blocksPaint().
     ============================================================ */

  D_MAX: 6,
  D_STEP: 1.25,                      // per toolbar click
  D_FIT_PAD: 10,                     // breathing room between a fitted plan and the panel
  dScale: 1, dTx: 0, dTy: 0,
  panTool: false,                    // hand tool: left-drag pans instead of painting.
                                     // SOURIS UNIQUEMENT — dSync le remet à faux au
                                     // doigt, où deux doigts remplacent la main.
  _dDrag: null, _dMoved: false,

  isDesktop:function(){ return !this.isMobile(); },

  /* True while a pan is (or has just been) in progress, or while the hand tool
     is armed. mosaik-controller.js asks before painting. */
  blocksPaint:function(){ return !!(this.panTool || this._dDrag || this._dMoved); },

  /* The box the plan is judged against for "does it still fit".

     In the split view the frame is NOT it anymore: #mosaik-canvas grows with the
     plan (a long floor scrolls the stage card rather than being clipped), so it
     always "fits" itself and every zoom-out button would read as disabled. The
     renderer's drawBox() is precisely the pane the plan was fitted into, so ask
     it rather than re-measuring the same thing a second way. Elsewhere (stacked
     tablet, mobile) the frame is still a fixed viewport — keep measuring it. */
  _fitBox:function(){
    const f = this.frame;
    const frameBox = { w: f ? f.clientWidth : 0, h: f ? f.clientHeight : 0 };
    if(!window.matchMedia('(min-width:1025px)').matches) return frameBox;
    if(!this.m || !this.m.drawBox) return frameBox;
    const b = this.m.drawBox();
    return (b && b.w > 0 && b.h > 0) ? b : frameBox;
  },

  /* Scale at which the WHOLE plan fits the panel — the floor of the zoom range.
     It is 1 for a plan that already fits, and below 1 for a plan the geometry
     had to render bigger than its pane (mosaik-geometry.js keeps tiles at least
     50px so they stay clickable, which on a long floor overflows the panel).
     That case is exactly what the "fit" button is for. */
  dMin:function(){
    const cv = this.cv;
    if(!this.frame || !cv || !cv.offsetWidth || !cv.offsetHeight) return 1;
    const box = this._fitBox(), p = 2 * this.D_FIT_PAD;
    return Math.min(1,
      Math.max(0.05, box.w - p) / cv.offsetWidth,
      Math.max(0.05, box.h - p) / cv.offsetHeight);
  },

  /* Is there anything off-screen to drag into view at the current scale? */
  dPannable:function(){
    const cv = this.cv, s = this.dScale;
    if(!this.frame || !cv) return false;
    const box = this._fitBox();
    return cv.offsetWidth * s > box.w + 1 || cv.offsetHeight * s > box.h + 1;
  },

  setupDesktop:function(){
    const self = this, cv = this.cv, frame = this.frame;
    if(!cv || !frame) return;
    const tools = document.getElementById('mosaik-zoom-tools');
    if(tools) tools.querySelectorAll('[data-zoom]').forEach(btn => {
      btn.addEventListener('click', () => self.dAction(btn.getAttribute('data-zoom')));
    });
    // wheel over the plan zooms it (the shell has its own scrolling panes, so the
    // page is not waiting for this wheel event)
    frame.addEventListener('wheel', e => self.onWheel(e), { passive:false });
    // pan: hand tool with the left button, or the middle button at any time
    frame.addEventListener('mousedown', e => self.onDownD(e));
    window.addEventListener('mousemove', e => self.onMoveD(e));
    window.addEventListener('mouseup',   e => self.onUpD(e));
    this.dSync();
  },

  dAction:function(kind){
    // la même barre, l'autre couche (voir la section « au doigt » ci-dessus)
    if(this.isMobile()) return this.mAction(kind);
    // "fit": the whole plan, centred — scaling below 1 when it does not fit
    if(kind === 'fit'){ this.dScale = this.dMin(); this.dTx = this.dTy = 0; this.dApplyClamped(); return; }
    if(kind === 'pan'){ this.panTool = !this.panTool; this.dSync(); return; }
    // zoom from the centre of the viewport, as the visitor would expect from a button
    const r = this.frame.getBoundingClientRect();
    this.dZoomAt(r.left + r.width / 2, r.top + r.height / 2,
                 kind === 'in' ? this.D_STEP : 1 / this.D_STEP);
  },

  /* The scrolling ancestor of the plan (split view: .mosaik-stage-card owns the
     vertical overflow while #mosaik-canvas clips). */
  _card:function(){
    const f = this.frame;
    return (f && f.closest) ? f.closest('.mosaik-stage-card') : null;
  },

  /* Does the stage card have somewhere to scroll? True exactly when the plan is
     taller than the pane — the case the split-view CSS turns into scroll. */
  _cardScrolls:function(){
    const card = this._card();
    return !!(card && card.scrollHeight - card.clientHeight > 1);
  },

  onWheel:function(e){
    if(!this.isDesktop()) return;
    // A wheel over a plan TALLER than the pane belongs to the scroll, not to the
    // zoom: on a long floor the first reflex is to scroll down it. Ctrl / ⌘ +
    // wheel still zooms (the usual map convention), as do the toolbar buttons.
    // A plan that fits keeps the plain wheel-zoom it always had.
    if(this._cardScrolls() && !e.ctrlKey && !e.metaKey) return;
    // fully zoomed out already: a scroll-up has nothing to do here — give the
    // wheel event back to the page instead of swallowing it
    if(this.dScale <= this.dMin() + 1e-4 && e.deltaY > 0) return;
    e.preventDefault();
    // deltaMode 1 = lines, 2 = pages: normalise so a trackpad and a wheel agree
    const step = e.deltaMode === 0 ? 1.0015 : 1.08;
    this.dZoomAt(e.clientX, e.clientY, Math.pow(step, -e.deltaY));
  },

  /* Zoom by `factor`, keeping the plan point under (clientX, clientY) put. */
  dZoomAt:function(clientX, clientY, factor){
    const s0 = this.dScale;
    const s = Math.max(this.dMin(), Math.min(this.D_MAX, s0 * factor));
    if(Math.abs(s - s0) < 1e-4) return;
    const r = this.cv.getBoundingClientRect();
    // untransformed origin of the canvas in client coords (transform is
    // translate then scale about 0 0, so the rect's corner is origin + t)
    const layoutLeft = r.left - this.dTx, layoutTop = r.top - this.dTy;
    const k = s / s0;
    this.dScale = s;
    this.dTx = clientX - layoutLeft - k * (clientX - layoutLeft - this.dTx);
    this.dTy = clientY - layoutTop  - k * (clientY - layoutTop  - this.dTy);
    this.dApplyClamped();
  },

  onDownD:function(e){
    if(!this.isDesktop()) return;
    const middle = e.button === 1;
    if(!middle && !(this.panTool && e.button === 0)) return;
    if(!this.dPannable() && !this._cardScrolls()) return; // nothing off-screen to reach
    e.preventDefault();
    // Verticalement le plan zoomé n'est PAS déplacé par la transform : le cadre
    // grandit avec le zoom (voir _dFrameHeight) et c'est la carte du plan qui
    // défile. On mémorise donc aussi son scrollTop pour que la main tire le plan
    // de haut en bas comme elle le tire de gauche à droite.
    const card = this._cardScrolls() ? this._card() : null;
    this._dDrag = { x:e.clientX, y:e.clientY, tx:this.dTx, ty:this.dTy,
                    card:card, top:card ? card.scrollTop : 0 };
    this._dMoved = false;
    this.frame.classList.add('mosaik-grabbing');
  },

  onMoveD:function(e){
    const d = this._dDrag;
    if(!d) return;
    e.preventDefault();
    this._dMoved = true;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    this.dTx = d.tx + dx;
    this.dTy = d.ty + dy;
    this.dApplyClamped();
    // le reste du geste vertical part dans le défilement de la carte : les deux
    // sont mesurés depuis le point de départ du drag, donc jamais cumulatifs
    if(d.card) d.card.scrollTop = d.top - dy;
  },

  onUpD:function(){
    if(!this._dDrag) return;
    this._dDrag = null;
    this.frame.classList.remove('mosaik-grabbing');
    // the click that closes this drag fires next — keep blocksPaint() true for it
    const self = this;
    setTimeout(() => { self._dMoved = false; }, 0);
  },

  /* La fenêtre VISIBLE du plan dans le panneau : celui-ci réserve en padding les
     bandes des règles, où le plan n'a rien à faire au repos. Repli sur la boîte
     entière si le renderer déployé est antérieur aux règles. */
  _viewBox:function(){
    const f = this.frame;
    const inner = (this.m && this.m.frameInner) ? this.m.frameInner(f) : null;
    return inner
      ? { x:inner.padL, y:inner.padT, w:inner.w, h:inner.h }
      : { x:0, y:0, w:f.clientWidth, h:f.clientHeight };
  },

  /* Keep the plan inside the viewport: pinned to the edges once it is bigger,
     re-centred while it still fits. La fenêtre est la boîte visible (hors bandes
     des règles) : un plan zoomé s'arrête donc SOUS la règle du haut au lieu de
     se ranger derrière elle, et ne cache jamais de dalle qu'on ne puisse ramener. */
  dClamp:function(){
    const cv = this.cv, s = this.dScale, b = this._viewBox();
    const fit = (o, size, start, len, t) => size <= len
      ? start + (len - size) / 2 - o                              // smaller: centre it
      : Math.min(start - o, Math.max(start + len - o - size, t)); // bigger: no gap at the edges
    this.dTx = fit(cv.offsetLeft, cv.offsetWidth  * s, b.x, b.w, this.dTx);
    this.dTy = fit(cv.offsetTop,  cv.offsetHeight * s, b.y, b.h, this.dTy);
  },

  /* Desktop: the frame's height follows the SCALED height of the plan, so the
     scroll range of the stage card always matches what is actually drawn — a
     zoomed-out plan leaves no blank strip under it, and a zoomed-in one is
     reached by scrolling down instead of by panning. Cleared at 1:1, where the
     CSS owns the height (grow to the plan, stretch when the plan is shorter
     than the pane). */
  _dFrameHeight:function(){
    if(!this.frame || !this.cv || !this.isDesktop()) return;
    const identity = Math.abs(this.dScale - 1) < 1e-4;
    this.frame.style.height = identity ? ''
      : (this.cv.offsetHeight * this.dScale + this._padY()) + 'px';
  },

  dApplyClamped:function(){
    this._dFrameHeight();
    this.dClamp();
    // untouched state (1:1, no offset) => no transform at all, so nothing about
    // the plan differs from before this layer existed
    const identity = Math.abs(this.dScale - 1) < 1e-4
                  && Math.abs(this.dTx) < .5 && Math.abs(this.dTy) < .5;
    this.cv.style.transform = identity ? ''
      : 'translate(' + this.dTx + 'px,' + this.dTy + 'px) scale(' + this.dScale + ')';
    this.dSync();
    this.reRender();
  },

  /* Toolbar state. Each button is disabled exactly when it would do nothing —
     and the hand disarms itself when there is nothing left to pan, so clicks go
     straight back to painting.

     Une seule barre, deux couches : on lit celle qui tient le zoom sur cet
     écran. Au doigt il n'y a pas de carte qui défile — le plan tient dans le
     panneau ou il en dépasse, et c'est tout. */
  dSync:function(){
    /* Les règles sont fixées au panneau, pas au plan : à chaque fois que le plan
       bouge sous elles (pincement, molette, main, « ajuster », retour à 1:1),
       leurs graduations changent. dSync est le point de passage obligé des DEUX
       couches — c'est donc ici, et nulle part ailleurs, qu'on les rafraîchit. */
    if(this.m && this.m.syncRulers) this.m.syncRulers();
    const tools = document.getElementById('mosaik-zoom-tools');
    const mobile = this.isMobile();
    // la main sert aussi quand seul le défilement vertical de la carte a de quoi
    // bouger (plan plus haut que le volet) — voir onMoveD
    const min      = mobile ? this.mMin()     : this.dMin(),
          scale    = mobile ? this.scale      : this.dScale,
          pannable = mobile ? this.mPannable() : (this.dPannable() || this._cardScrolls());
    const canOut = scale > min + 1e-4;
    // la main n'existe qu'à la souris : au doigt, deux doigts déplacent le plan et
    // un doigt peint, sans mode à armer. Le désarmement est fait ici plutôt qu'à la
    // bascule de largeur, pour qu'un écran redimensionné (ou pivoté) ne garde pas
    // une main armée invisible qui empêcherait de peindre.
    if(mobile || !pannable) this.panTool = false;
    if(this.frame) this.frame.classList.toggle('mosaik-grab', this.panTool);
    if(!tools) return;
    const btn = k => tools.querySelector('[data-zoom="' + k + '"]');
    const out = btn('out'), pan = btn('pan'), zin = btn('in'), fit = btn('fit');
    if(out) out.disabled = !canOut;
    // "fit" is also the way to pull an over-sized plan back into the panel, so
    // it stays available at 1:1 whenever the plan does not fit as drawn
    if(fit) fit.disabled = !canOut && !pannable;
    if(zin) zin.disabled = this.dScale >= this.D_MAX - 1e-4;
    if(pan){
      pan.disabled = !pannable;
      pan.classList.toggle('active', this.panTool);
      pan.setAttribute('aria-pressed', this.panTool ? 'true' : 'false');
    }
    // La main armée met le dessin en pause : la pastille passe au bleu plein,
    // le panneau prend son liseré (`.mosaik-grab`, plus haut) et la mention
    // « dessin en pause » paraît à côté des outils — sans quoi on tape sur une
    // dalle qui ne se peint pas et on croit l'outil cassé. Le CSS s'en charge
    // à partir de cette seule classe (voir .mosaik-pan-note dans le template).
    tools.classList.toggle('mosaik-pan-on', this.panTool);
  }
};
